import { isClosedStatus, type SetUserTaskStatusReq, type UserTask } from "@/types/userTasks.ts";
import {
  GOT_IT,
  KEEP_TASK,
  REOPEN_TASK,
  TAKE_IT_BACK,
  TASK_DONE_SHORT,
  TO_OKTHX,
} from "./taskRowCopy.ts";
import { commitDays, commitOnDay } from "./commitDates.ts";

const STATUS_ROUTE_STATUSES = ["open", "later", "done", "cancelled"] as const;

/**
 * The status a row shows, as the status route's `fromStatus`: the server
 * answers 409 when the task changed since, rather than moving it again.
 */
export function shownStatus(status: string): SetUserTaskStatusReq["fromStatus"] {
  return STATUS_ROUTE_STATUSES.find((s) => s === status);
}

/**
 * Whether a task has been settled rather than parked: closed, and not
 * dismissed, so the way back is the only answer it has left.
 *
 * Narrower than {@link canReopen}, which a snoozed task also satisfies. That
 * is the difference the surfaces kept getting wrong: a snoozed task is parked
 * on a date and still live — it can be finished early or dropped on the day it
 * is read, without first being brought back — where a closed one has had the
 * question answered, and asking it again lets the second answer overwrite the
 * first. So a surface that collapses to a lone Reopen asks this, and only one
 * offering Reopen among other answers asks `canReopen`.
 */
export function isSettled(task: UserTask): boolean {
  return isClosedStatus(task.status) && !task.dismissed;
}

/**
 * Whether a task has a way back to the list: a snooze, or a close the user did
 * not dismiss.
 *
 * A dismissed task is the record of an answer rather than something paused, so
 * there is nothing to return it to. Shared because the row, the card and web
 * all ask it, and three copies of one rule drift the moment a fourth status
 * earns a way back.
 */
export function canReopen(task: UserTask): boolean {
  // Checked first, whatever the status: dismissed never comes back.
  if (task.dismissed) return false;
  return task.status === "later" || isClosedStatus(task.status);
}

/**
 * 9am tomorrow, in the viewer's own time.
 *
 * The only horizon offered. "Next week" asked the user to choose how long to
 * put a task down for, when the answer to a task they are not doing today is
 * the same either way — and a task put down until tomorrow can be put down
 * again tomorrow.
 */
export function laterAt(now: Date = new Date()): number {
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return commitOnDay(tomorrow);
}

/**
 * The thread holding what okthx has asked, where it is waiting on the reader.
 *
 * Returns the thread rather than a boolean, because every caller that asks
 * the question then needs the thread itself — to open it, or to navigate to
 * it — and a flag would make each one re-read the field and re-narrow it.
 *
 * `needsInput` rather than the Needs your input group, which also holds the
 * reader's own tasks: ones nobody is blocked on, where there is nothing to
 * answer and handing the task to okthx is the move that remains.
 */
export function answerableThreadId(task: UserTask | undefined): number | null {
  if (!task?.needsInput) return null;
  return task.threadId ?? null;
}

/**
 * The one answer that moves a task forward, which is a different answer for
 * each kind of card.
 *
 * Every card offers exactly one, opposite the ways off the list, and each
 * names who moves it next: either the reader takes it on, or okthx does. The
 * owner menu this replaced asked a third question — who should hold it —
 * which on most cards was already settled, and on a suggestion was being asked
 * about a task the reader had not yet agreed existed.
 *
 * Decided here rather than per surface: the row and the card answer the same
 * question on the same task, and two copies of this ladder drift the first
 * time a state is added.
 */
export type AffirmativeKind =
  "keep" | "answer" | "commit" | "takeBack" | "toOkthx" | "done" | "reopen";

export function taskAffirmative(task: UserTask): AffirmativeKind | null {
  // A suggestion delegates the whole question: okthx decides whether to work
  // it or hand it back, which is what makes it the one high-level answer.
  if (task.status === "suggested") return "keep";
  // Already parked on a date it is coming back on. The only move left is
  // having finished it early.
  if (task.status === "later") return "done";
  // Answered: the way back is the one move, and a dismissal has none.
  if (isClosedStatus(task.status)) return canReopen(task) ? "reopen" : null;
  // Okthx has actually asked: nobody else can unblock it.
  if (answerableThreadId(task) !== null) return "answer";
  // Okthx holds it and is working, so taking it on means stopping that.
  if (task.assignee === "okthx") return "takeBack";
  // The reader is already on it: the only motion left is having finished.
  if (task.heldAtMs) return "done";
  // Waiting on a person: okthx drafts the chase, and it comes back for
  // approval before anything is sent.
  if (task.waitingOn) return "toOkthx";
  // The reader's own, with nothing asked and nobody blocked: they take it
  // on, either as already in hand or for a day they name. Always openable,
  // since In progress needs no day — a task due this afternoon is the one
  // most likely to be in hand right now — so the calendar alone goes quiet
  // when no day comes before it is due.
  return "commit";
}

/**
 * What each affirmative is called, in one place so a row and a card cannot
 * name the same answer differently — which they did for a year, across four
 * words for handing a task over.
 */
export const AFFIRMATIVE_LABEL: Record<AffirmativeKind, string> = {
  keep: KEEP_TASK,
  answer: GOT_IT,
  commit: GOT_IT,
  takeBack: TAKE_IT_BACK,
  toOkthx: TO_OKTHX,
  done: TASK_DONE_SHORT,
  reopen: REOPEN_TASK,
};

/**
 * What a row does for each affirmative that is one tap. `commit` is left out:
 * it asks for a day first, so it is a menu of its own rather than a button.
 * The row supplies these, and the platform's `Affirmative` draws the one
 * `taskAffirmative` picks — so neither platform carries the switch.
 */
export type AffirmativeHandlers = Record<Exclude<AffirmativeKind, "commit">, () => void>;

/**
 * What a screen reader hears for the affirmative where its label alone is not
 * enough: "Keep", read down a list of suggestions, does not say which one, so
 * Keep is named with the task. The rest are read as they are drawn.
 */
export function affirmativeAccessibleName(
  kind: AffirmativeKind,
  task: Pick<UserTask, "title">,
): string | undefined {
  return kind === "keep" ? `${AFFIRMATIVE_LABEL[kind]}: ${task.title}` : undefined;
}

/**
 * When Snooze puts a task back, or none when that would be after the task is
 * due: a task for a party at 3:30pm today is not put off until tomorrow
 * (BIG-9170). An overdue task may be put off; the user knows it is late.
 *
 * The same rule a commitment follows, asked of its first day: a snooze is a
 * commitment to tomorrow with nothing said.
 */
export function snoozeUntil(
  task: Pick<UserTask, "dueMs" | "dueDate">,
  nowMs: number = Date.now(),
): number | undefined {
  const days = commitDays(task, nowMs);
  return days ? commitOnDay(days.first) : undefined;
}

/**
 * Whether Snooze is one of a task's ways out.
 *
 * Only a task the reader holds and could act on today. A suggestion is
 * answered in Triage, and the status route refuses to park one; a task
 * already parked on a date has that as its answer, and snoozing it again
 * would only move it; a task waiting on someone else, or one okthx is working
 * on, is not the reader's to put down, since nothing they would do tomorrow
 * is waiting on them; one they are on themselves has just said so; and
 * tomorrow morning has to come before the task is due, or it would be put
 * down past the thing it was for.
 */
export function canSnooze(task: UserTask, nowMs: number = Date.now()) {
  if (task.status !== "open") return false;
  if (task.assignee === "user" && (task.waitingOn || task.heldAtMs)) return false;
  if (task.assignee === "okthx" && !task.needsInput) return false;
  return snoozeUntil(task, nowMs) !== undefined;
}
